import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
  UniversalFilterBar,
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
} from '@sighthost/ui';
import { FileMinus } from 'lucide-react';
import {
  adminBillingService,
  type AdminCreditNoteRow,
} from '@/domains/billing/services/adminBillingService';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import type { ListWindow } from '@/shared/lib/listWindow';
import { formatDate, formatZAR } from '@/shared/utils/format';

export default function AdminCreditNotesPage() {
  const navigate = useNavigate();
  const [win, setWin] = useState<ListWindow<AdminCreditNoteRow>>({
    rows: [],
    truncated: false,
    limit: 200,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /** Credit notes are FLOW — the period is meaningful and labelled `Issued`. */
  const table = useTableFilters<AdminCreditNoteRow>({
    rows: win.rows,
    dateMode: 'server',
    dateLabel: 'Issued',
    searchText: (r) => [r.number, r.customer_name ?? '', r.reason ?? ''].join(' '),
    truncated: win.truncated,
    limit: win.limit,
  });

  const { start: rangeStart, end: rangeEnd } = table.range;

  useEffect(() => {
    let cancelled = false;
    adminBillingService
      .listCreditNotes({ range: { start: rangeStart, end: rangeEnd } })
      .then((data) => {
        if (!cancelled) {
          setWin(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rangeStart, rangeEnd]);

  const columns = useMemo<UniversalTableColumn<AdminCreditNoteRow>[]>(
    () => [
      {
        key: 'number',
        header: 'Credit note',
        sortValue: (r) => r.number,
        render: (r) => <code className="text-sm text-foreground">{r.number}</code>,
        width: 'w-40',
      },
      {
        // F-18. The list had room to spare and made you open a row to learn
        // whose credit it was — the first thing anyone wants to know.
        key: 'customer',
        header: 'Customer',
        sortValue: (r) => r.customer_name ?? '',
        render: (r) => (
          <span className={r.customer_name ? 'text-foreground' : 'text-muted-foreground'}>
            {r.customer_name ?? '—'}
          </span>
        ),
      },
      {
        key: 'issued',
        header: 'Issued',
        sortValue: (r) => new Date(r.issued_at).getTime(),
        // `formatDate` pins Africa/Johannesburg; the inline toLocaleDateString
        // this replaced did not.
        render: (r) => <span className="text-foreground">{formatDate(r.issued_at)}</span>,
      },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        sortValue: (r) => r.amount,
        render: (r) => <span className="font-semibold text-foreground">{formatZAR(r.amount)}</span>,
        width: 'w-32',
      },
      {
        key: 'reason',
        header: 'Reason',
        render: (r) => (
          <span className="text-sm text-muted-foreground">{r.reason ?? '—'}</span>
        ),
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Credit notes"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Billing', href: '/admin/billing' },
        { label: 'Credit notes' },
      ]}
    >
      <UniversalFilterBar
        period={
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Issued"
          />
        }
        extra={
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search number, customer, reason"
            className="w-full max-w-xs"
          />
        }
        activeCount={table.filters.activeCount + (table.q ? 1 : 0)}
        onClear={() => {
          table.filters.clear();
          table.setQ('');
        }}
      />

      <UniversalTable<AdminCreditNoteRow>
        data={table.rows}
        columns={columns}
        getRowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/admin/customers/${r.customer_id}/billing`)}
        isLoading={loading}
        error={error}
        caption={loading ? undefined : table.caption}
        empty={{
          icon: FileMinus,
          title: 'No credit notes yet',
          description:
            'Credit notes are issued via Billing → Payments → Record refund (partial / goodwill) and appear here.',
        }}
      />
    </UniversalPage>
  );
}
